// V3 Programs Console — plans + plan_items tree + goals su /api/v2
// (cap_programs). Contenuto + progressione: workout/nutrition/program
// plans con albero item ricorsivo e obiettivi tracciati.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });
const badge = (s: string): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
  background: s === 'active' ? '#dcfce7' : s === 'completed' || s === 'achieved' ? '#dbeafe' : '#f3f4f6',
  color: s === 'active' ? '#166534' : s === 'completed' || s === 'achieved' ? '#1e40af' : '#4b5563',
});

const KINDS = ['workout', 'nutrition', 'program', 'package'];
const ITEM_TYPES = ['week', 'day', 'exercise', 'meal', 'treatment_link', 'program_link'];

export function V3ProgramsPage() {
  const [plans, setPlans] = useState<Row[]>([]);
  const [goals, setGoals] = useState<Row[]>([]);
  const [customers, setCustomers] = useState<Row[]>([]);
  const [selPlan, setSelPlan] = useState<Row | null>(null);
  const [items, setItems] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [kind, setKind] = useState('workout');
  const [planForm, setPlanForm] = useState({ title: '', description: '', profile_id: '' });
  const [itemForm, setItemForm] = useState({ parent_id: '', item_type: 'day', title: '', spec: '' });
  const [goalForm, setGoalForm] = useState({ profile_id: '', title: '', target_value: '', target_unit: 'kg', deadline: '' });

  const load = useCallback(async () => {
    try {
      const [p, g, c] = await Promise.all([
        apiFetch(`/api/v2/programs-plans?kind=${kind}`),
        apiFetch('/api/v2/programs-goals'), apiFetch('/api/v2/customers'),
      ]);
      setPlans(await p.json()); setGoals(await g.json());
      const cd = await c.json();
      setCustomers(Array.isArray(cd) ? cd : cd.customers || []);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, [kind]);

  const loadItems = useCallback(async (plan: Row) => {
    try {
      const r = await apiFetch(`/api/v2/programs-plan-items?plan_id=${plan.id}`);
      setItems(await r.json());
      setSelPlan(plan);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore items');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const createPlan = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/programs-plans', {
        method: 'POST',
        body: JSON.stringify({
          kind, title: planForm.title, description: planForm.description || null,
          profile_id: planForm.profile_id ? Number(planForm.profile_id) : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setPlanForm({ title: '', description: '', profile_id: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore piano');
    } finally {
      setSaving(false);
    }
  };

  const addItem = async () => {
    if (!selPlan) return;
    setSaving(true); setError('');
    try {
      let spec: Record<string, unknown> = {};
      try { spec = JSON.parse(itemForm.spec || '{}'); }
      catch { throw new Error('Spec deve essere JSON valido'); }
      const res = await apiFetch('/api/v2/programs-plan-items', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: selPlan.id, parent_id: itemForm.parent_id ? Number(itemForm.parent_id) : null,
          item_type: itemForm.item_type, title: itemForm.title || null, spec,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setItemForm({ parent_id: '', item_type: 'day', title: '', spec: '' });
      await loadItems(selPlan);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore item');
    } finally {
      setSaving(false);
    }
  };

  const createGoal = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/programs-goals', {
        method: 'POST',
        body: JSON.stringify({
          profile_id: Number(goalForm.profile_id), title: goalForm.title,
          target_value: goalForm.target_value ? Number(goalForm.target_value) : null,
          target_unit: goalForm.target_unit || null, deadline: goalForm.deadline || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setGoalForm({ profile_id: '', title: '', target_value: '', target_unit: 'kg', deadline: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore goal');
    } finally {
      setSaving(false);
    }
  };

  const profileName = (id: unknown) =>
    customers.find((c) => c.id === Number(id))?.name || customers.find((c) => c.id === Number(id))?.display_name || (id ? `#${id}` : '—');

  const childItems = items.filter((i) => !i.parent_id);

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuovo piano</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input style={input} placeholder="Titolo *" value={planForm.title}
              onChange={(e) => setPlanForm({ ...planForm, title: e.target.value })} />
            <input style={input} placeholder="Descrizione" value={planForm.description}
              onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            <select style={input} value={planForm.profile_id} onChange={(e) => setPlanForm({ ...planForm, profile_id: e.target.value })}>
              <option value="">Assegnatario (opz.)…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.display_name || `#${c.id}`}</option>)}
            </select>
            <button style={btn()} disabled={saving || !planForm.title} onClick={() => void createPlan()}>
              {saving ? '…' : 'Crea piano'}
            </button>
          </div>
        </div>

        {selPlan && (
          <div style={card}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Item su «{selPlan.title}»</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              <select style={input} value={itemForm.item_type} onChange={(e) => setItemForm({ ...itemForm, item_type: e.target.value })}>
                {ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select style={input} value={itemForm.parent_id} onChange={(e) => setItemForm({ ...itemForm, parent_id: e.target.value })}>
                <option value="">Parent (radice)…</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.item_type} {i.title || i.id}</option>)}
              </select>
              <input style={input} placeholder="Titolo item" value={itemForm.title}
                onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} />
              <input style={input} placeholder='Spec JSON {"sets":4,"reps":8}' value={itemForm.spec}
                onChange={(e) => setItemForm({ ...itemForm, spec: e.target.value })} />
              <button style={btn('#0d6efd')} disabled={saving} onClick={() => void addItem()}>
                {saving ? '…' : 'Aggiungi item'}
              </button>
            </div>
          </div>
        )}

        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuovo obiettivo</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={goalForm.profile_id} onChange={(e) => setGoalForm({ ...goalForm, profile_id: e.target.value })}>
              <option value="">Cliente…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.display_name || `#${c.id}`}</option>)}
            </select>
            <input style={input} placeholder="Obiettivo *" value={goalForm.title}
              onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={input} placeholder="Target" type="number" value={goalForm.target_value}
                onChange={(e) => setGoalForm({ ...goalForm, target_value: e.target.value })} />
              <input style={{ ...input, width: 80 }} value={goalForm.target_unit}
                onChange={(e) => setGoalForm({ ...goalForm, target_unit: e.target.value })} />
            </div>
            <input style={input} placeholder="Deadline YYYY-MM-DD" value={goalForm.deadline}
              onChange={(e) => setGoalForm({ ...goalForm, deadline: e.target.value })} />
            <button style={btn('#166534')} disabled={saving || !goalForm.profile_id || !goalForm.title} onClick={() => void createGoal()}>
              {saving ? '…' : 'Crea obiettivo'}
            </button>
          </div>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {plans.length} piani · {goals.length} obiettivi
        </p>
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Piani {kind} ({plans.length})</h4>
          {plans.length === 0 && <p className="sk-muted">Nessun piano.</p>}
          {plans.map((p) => (
            <div key={p.id} style={{ padding: '10px 0', borderTop: '1px solid var(--sk-border, #eee)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                <span><strong>{p.title}</strong> · {profileName(p.profile_id)}</span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={badge(p.status)}>{p.status}</span>
                  <button style={{ ...btn('#e5e7eb'), color: '#111', padding: '2px 10px' }} onClick={() => void loadItems(p)}>Albero</button>
                </span>
              </div>
              {selPlan?.id === p.id && childItems.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  {childItems.map((i) => {
                    const kids = items.filter((k) => k.parent_id === i.id);
                    return (
                      <div key={i.id} style={{ marginTop: 4 }}>
                        <span>▸ {i.item_type}: <strong>{i.title || '—'}</strong>
                          {i.is_completed ? ' ✓' : ''}
                          {Object.keys(i.spec || {}).length ? ` · ${JSON.stringify(i.spec)}` : ''}</span>
                        {kids.map((k) => (
                          <div key={k.id} style={{ marginLeft: 16 }}>
                            • {k.item_type}: {k.title || '—'}
                            {Object.keys(k.spec || {}).length ? ` · ${JSON.stringify(k.spec)}` : ''}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
              {selPlan?.id === p.id && childItems.length === 0 && (
                <p className="sk-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>Nessun item — aggiungi dal form a sinistra.</p>
              )}
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Obiettivi ({goals.length})</h4>
          {goals.length === 0 && <p className="sk-muted">Nessun obiettivo.</p>}
          {goals.map((g) => (
            <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{g.title}</strong> · {profileName(g.profile_id)}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: '#6b7280' }}>{g.current_value ?? '—'}→{g.target_value} {g.target_unit}</span>
                <span style={badge(g.status)}>{g.status}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
